import { useQuery } from '@apollo/client/react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { AccessNotice } from '@/components/AccessNotice/AccessNotice';
import { Callout } from '@/components/Callout/Callout';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { Spinner } from '@/components/Spinner/Spinner';
import { Tabs, type TabItem } from '@/components/Tabs/Tabs';
import { BROKERAGE_QUERY, type BrokerageResult } from '@/graphql/brokerages';
import { ME_QUERY, type MeQueryResult } from '@/graphql/keystone';
import { brokerageName } from '@/lib/brokerages';
import { friendlyError } from '@/lib/keystone-errors';
import { useBrokerageAccess, type BrokerageAccess } from '@/lib/use-brokerage-access';

import { BrandingSection } from './brokerage/BrandingSection';
import { ConfigurationSection } from './brokerage/ConfigurationSection';
import { MembersSection } from './brokerage/MembersSection';
import { RolesSection } from './brokerage/RolesSection';
import styles from './BrokeragesContainer.module.css';

const TABS: readonly (TabItem & { key: keyof BrokerageAccess })[] = [
  { id: 'members', label: 'Members', key: 'members' },
  { id: 'roles', label: 'Roles & permissions', key: 'access_control' },
  { id: 'branding', label: 'Branding', key: 'organisation_config' },
  { id: 'setup', label: 'Case & task setup', key: 'organisation_config' },
];

const readable = (state: string) => state === 'read' || state === 'full';

/**
 * `/brokerages/:id`: one brokerage you manage. Each tab follows what your
 * portfolio grants there, asked of Keystone for this brokerage on load.
 */
export function BrokerageContainer() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { access, refetch: recheckAccess } = useBrokerageAccess(id);
  const me = useQuery<MeQueryResult>(ME_QUERY);
  const brokerage = useQuery<BrokerageResult>(BROKERAGE_QUERY, { variables: { tenantId: id } });

  const checking = Object.values(access).some((state) => state === 'checking');
  const unavailable = Object.values(access).some((state) => state === 'unavailable');
  const tabs = TABS.filter((tab) => readable(access[tab.key]));
  const selected = tabs.find((tab) => tab.id === params.get('tab')) ?? tabs[0];

  const back = <Link to="/brokerages">← All brokerages</Link>;

  if (brokerage.error && !brokerage.data) {
    return (
      <>
        <PageHeader back={back} title="Brokerage" />
        <Callout tone="danger" title="This brokerage couldn't be loaded">
          {friendlyError(brokerage.error)}
        </Callout>
      </>
    );
  }

  const name = brokerage.data ? brokerageName(brokerage.data.brokerage) : 'Brokerage';
  const meId = me.data?.me.id ?? null;
  const tenantId = id;
  const onAccessChanged = recheckAccess;

  const panel = () => {
    if (!selected) {
      return null;
    }
    const canEdit = (key: keyof BrokerageAccess) => access[key] === 'full';
    switch (selected.id) {
      case 'members':
        return (
          <MembersSection
            canEdit={canEdit('members')}
            canReadRoles={readable(access.access_control)}
            meId={meId}
            onAccessChanged={onAccessChanged}
            tenantId={tenantId}
          />
        );
      case 'roles':
        return (
          <RolesSection
            canEdit={canEdit('access_control')}
            canReadMembers={readable(access.members)}
            meId={meId}
            onAccessChanged={onAccessChanged}
            tenantId={tenantId}
          />
        );
      case 'branding':
        return (
          <BrandingSection
            canEdit={canEdit('organisation_config')}
            onAccessChanged={onAccessChanged}
            tenantId={tenantId}
          />
        );
      default:
        return (
          <ConfigurationSection
            canEdit={canEdit('organisation_config')}
            onAccessChanged={onAccessChanged}
            tenantId={tenantId}
          />
        );
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader back={back} title={name} />
      {checking || brokerage.loading ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading…
        </p>
      ) : tabs.length === 0 ? (
        <AccessNotice
          area="this brokerage"
          onRetry={recheckAccess}
          state={unavailable ? 'unavailable' : 'none'}
        />
      ) : (
        <Tabs
          label={name}
          onSelect={(tab) => setParams({ tab }, { replace: true })}
          selected={selected.id}
          tabs={tabs}
        >
          {panel()}
        </Tabs>
      )}
    </div>
  );
}
